<template>
  <b-modal :active="true" full-screen>
    <div class="stl-viewer-container">
      <div
        ref="viewerContainer"
        class="stl-viewer"
        @drop.prevent="handleDrop"
        @dragover.prevent
        @dragenter.prevent
      >
        <div v-if="!modelLoaded" class="drop-hint">
          <template v-if="showDonwloadProgress">
            <p>{{ $t('viewer_3d.downloading_file') }}</p>
            <progress :value="downloadProgress" max="100"></progress>
          </template>

          <template v-else> {{ $t('viewer_3d.loading_file') }} </template>
        </div>
        <div class="controls" v-if="!showTasksModal">
          <b-button @click="moveLeft" icon-right="arrow-left-bold-outline" />
          <b-button @click="moveUp" icon-right="arrow-up-bold-outline" />
          <b-button @click="moveDown" icon-right="arrow-down-bold-outline" />
          <b-button @click="moveRight" icon-right="arrow-right-bold-outline" />
          <b-button @click="takeScreenshot" icon-left="monitor-screenshot">{{
            $t('viewer_3d.screenshot')
          }}</b-button>
        </div>
      </div>
    </div>
    <TasksSelectModal
      v-if="showTasksModal"
      @close="() => (showTasksModal = false)"
      @save="onSaveScreenshot"
      :loadingProps="taskModalLoading"
    />
    <footer class="modal-card-foot">
      <b-button @click="close">
        {{ $t('close') }}
      </b-button>
    </footer>
  </b-modal>
</template>

<script>
import * as THREE from 'three';
import { TrackballControls } from 'three/examples/jsm/controls/TrackballControls';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import client from '../../../config/api';
import DIJVueConfigurations from '../../../helpers/DIJVueConfigurations';

import TasksSelectModal from './TasksSelectModal';

export default {
  name: 'Viewer3D',
  components: {
    TasksSelectModal,
  },
  props: {
    filePath: {
      type: String,
      default: null,
    },
    displayFileName: {
      type: String,
      default: null,
    },
  },

  computed: {
    job() {
      return this.$store.state.currentJob;
    },
  },

  data() {
    return {
      scene: null,
      camera: null,
      renderer: null,
      controls: null,
      model: null,
      modelLoaded: false,
      loader: new STLLoader(),
      container: null,
      showTasksModal: false,
      base64ImageToUpload: null,
      taskModalLoading: false,
      directionalLight: null,
      animateFrameId: null,
      downloadProgress: 0,
      showDonwloadProgress: false,
    };
  },
  async mounted() {
    this.initThreeJS();

    const file = await this.downloadSTL(this.getFileUrl(this.filePath));
    if (file) {
      this.loadSTL(file);
    }
    window.addEventListener('keydown', this.handleArrowKeys);
  },
  beforeDestroy() {
    if (this.controls) this.controls.dispose();
    if (this.renderer) this.renderer.dispose();
    window.removeEventListener('keydown', this.handleArrowKeys);
  },
  methods: {
    async onSaveScreenshot(taskId) {
      this.taskModalLoading = true;
      const baseName = this.displayFileName.replace(/\.stl$/i, '');
      const fileName = `${baseName}.png`;
      const response = await client.post(
        `/job/${this.job._id}/presign-upload-file`,
        { fileName, taskId }
      );

      const base64Data = this.base64ImageToUpload.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const file = new File([byteArray], fileName, { type: 'image/png' });

      const { presignedPostData, path, uniqueFileName } = response.data;
      const dijConfigurations = new DIJVueConfigurations();
      // eslint-disable-next-line no-await-in-loop
      await dijConfigurations.sendPresignedPostData(
        presignedPostData,
        file,
        fileName
      );

      const payload = {
        displayName: fileName,
        filePath: path,
        uniqueFileName,
      };

      await client.post(
        `/job/${this.job._id}/task/${taskId}/reportfile`,
        payload
      );

      this.taskModalLoading = false;
      this.showTasksModal = false;
    },

    takeScreenshot() {
      if (!this.renderer || !this.scene || !this.camera) return;

      // Manually render one frame before capturing
      this.renderer.render(this.scene, this.camera);

      // Delay capture slightly to ensure frame has been rendered
      this.$nextTick(() => {
        this.base64ImageToUpload =
          this.renderer.domElement.toDataURL('image/png');

        this.showTasksModal = true;
      });
    },

    handleArrowKeys(event) {
      if (!this.model) return;

      switch (event.key) {
        case 'ArrowLeft':
          this.moveLeft();
          event.preventDefault();
          break;
        case 'ArrowRight':
          this.moveRight();
          event.preventDefault();
          break;
        case 'ArrowUp':
          this.moveUp();
          event.preventDefault();
          break;
        case 'ArrowDown':
          this.moveDown();
          event.preventDefault();
          break;

        default:
          break;
      }
    },

    getFileUrl(url) {
      if (!url) return null;
      if (Array.from(url)[0] !== '/') url = `/${url}`;

      const lastPart = url.split('/').pop();
      const allButLast = url.split('/').slice(0, -1).join('/');
      url = `${allButLast}/${encodeURIComponent(lastPart)}`;

      // For local env we need to point to the local s3
      if (window.location.host.includes('localhost')) {
        return `http://localhost:4566/images${url}`;
      }

      return url;
    },

    async downloadSTL(url) {
      try {
        this.showDonwloadProgress = true;
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to download STL file');

        const contentLength = +response.headers.get('Content-Length');
        if (!contentLength) throw new Error('Cannot determine content length');

        const reader = response.body.getReader();
        const chunks = [];
        let receivedLength = 0;

        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { done, value } = await reader.read();
          if (done) break;

          chunks.push(value);
          receivedLength += value.length;
          this.downloadProgress = Math.floor(
            (receivedLength / contentLength) * 100
          );
        }

        const blob = new Blob(chunks);
        const file = new File([blob], 'downloaded.stl', {
          type: blob.type || 'application/sla',
        });
        this.showDonwloadProgress = false;
        return file;
      } catch (err) {
        console.error('Error downloading STL:', err);
      }
    },

    initThreeJS() {
      this.container = this.$refs.viewerContainer;

      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0xf0f0f0);

      // const ambientLight = new THREE.AmbientLight(0xffffff, 0);
      // this.scene.add(ambientLight);

      this.directionalLight = new THREE.DirectionalLight(0xffffff, 1.2);
      this.directionalLight.position.set(50, 100, 100);

      this.directionalLight.castShadow = true;
      this.directionalLight.shadow.bias = -0.001;
      this.directionalLight.shadow.mapSize.width = 4096;
      this.directionalLight.shadow.mapSize.height = 4096;
      this.directionalLight.shadow.camera.near = 0.1;
      this.directionalLight.shadow.camera.far = 1000;
      this.directionalLight.shadow.camera.left = -200;
      this.directionalLight.shadow.camera.right = 200;
      this.directionalLight.shadow.camera.top = 200;
      this.directionalLight.shadow.camera.bottom = -200;
      this.scene.add(this.directionalLight);
      this.scene.add(this.directionalLight.target);

      const fillLight = new THREE.DirectionalLight(0xffffff, 0.1);
      fillLight.position.set(-1, -1, -2);
      this.scene.add(fillLight);

      const width = this.container.clientWidth;
      const height = this.container.clientHeight;
      const aspect = width / height;
      const frustumSize = 500;

      const floorGeometry = new THREE.PlaneGeometry(1000, 1000);
      const floorMaterial = new THREE.ShadowMaterial({ opacity: 0.3 });
      const floor = new THREE.Mesh(floorGeometry, floorMaterial);
      floor.rotation.x = -Math.PI / 2;
      floor.position.y = -50;
      floor.receiveShadow = true;
      this.scene.add(floor);

      this.camera = new THREE.OrthographicCamera(
        (frustumSize * aspect) / -2,
        (frustumSize * aspect) / 2,
        frustumSize / 2,
        frustumSize / -2,
        -5000,
        5000
      );
      this.camera.position.set(0, 0, 1000);
      this.camera.lookAt(0, 0, 0);

      this.renderer = new THREE.WebGLRenderer({ antialias: true });
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      this.container.appendChild(this.renderer.domElement);

      this.controls = new TrackballControls(
        this.camera,
        this.renderer.domElement
      );
      this.controls.rotateSpeed = 5.0;
      this.controls.zoomSpeed = 2.2;
      this.controls.panSpeed = 1.0;
      this.controls.noZoom = false;
      this.controls.noPan = false;
      this.controls.staticMoving = true;

      this.animate();
    },

    animate() {
      this.animateFrameId = requestAnimationFrame(this.animate);
      if (this.controls) this.controls.update();

      if (this.directionalLight && this.camera) {
        this.directionalLight.position.copy(this.camera.position);
        this.directionalLight.target.position.set(0, 0, 0);
        this.directionalLight.target.updateMatrixWorld();
      }

      if (this.renderer && this.scene && this.camera) {
        this.renderer.render(this.scene, this.camera);
      }
    },

    loadSTL(file) {
      if (!(file instanceof Blob)) {
        console.error('loadSTL: Provided file is not a Blob/File:', file);
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        // Remove previous model if exists
        if (this.model) {
          this.scene.remove(this.model);
          this.model.geometry.dispose();
          this.model.material.dispose();
        }

        // Load new geometry
        const geometry = this.loader.parse(event.target.result);

        // Compute bounding box and center
        geometry.computeBoundingBox();
        const { boundingBox } = geometry;
        const center = new THREE.Vector3();
        boundingBox.getCenter(center);
        geometry.translate(-center.x, -center.y, -center.z); // center the model

        geometry.rotateX(-Math.PI / 2); // adjust based on your STL orientation

        const material = new THREE.MeshPhongMaterial({
          color: 0xffffff,
          shininess: 10,
          specular: 0x222222,
          side: THREE.DoubleSide,
        });

        this.model = new THREE.Mesh(geometry, material);
        this.scene.add(this.model);

        this.model.castShadow = true;
        this.model.receiveShadow = true;

        // Calculate bounding sphere to adjust camera
        geometry.computeBoundingSphere();
        const { radius } = geometry.boundingSphere;

        // Adjust Orthographic camera to fit model
        const aspect = this.container.clientWidth / this.container.clientHeight;
        const zoomFactor = 1.5;

        this.camera.left = -radius * aspect * zoomFactor;
        this.camera.right = radius * aspect * zoomFactor;
        this.camera.top = radius * zoomFactor;
        this.camera.bottom = -radius * zoomFactor;
        this.camera.near = -radius * 10;
        this.camera.far = radius * 10;
        this.camera.updateProjectionMatrix();

        // Position the camera and look at center
        this.camera.position.set(0, 0, radius * 3);
        this.controls.target.set(0, 0, 0);
        this.controls.update();

        this.modelLoaded = true;
      };
      reader.readAsArrayBuffer(file);
    },

    moveLeft() {
      if (this.model && this.camera) {
        // Get camera direction (right vector)
        const cameraDirection = new THREE.Vector3();
        this.camera.getWorldDirection(cameraDirection);

        // Get camera's right vector
        const up = this.camera.up.clone().normalize();
        const right = new THREE.Vector3()
          .crossVectors(cameraDirection, up)
          .normalize();

        // Move model to the left relative to camera (negative right vector)
        this.model.position.addScaledVector(right, -10);
      }
    },
    moveRight() {
      if (this.model && this.camera) {
        // Get camera direction (right vector)
        const cameraDirection = new THREE.Vector3();
        this.camera.getWorldDirection(cameraDirection);

        // Get camera's right vector
        const up = this.camera.up.clone().normalize();
        const right = new THREE.Vector3()
          .crossVectors(cameraDirection, up)
          .normalize();

        // Move model to the left relative to camera (negative right vector)
        this.model.position.addScaledVector(right, 10);
      }
    },
    moveUp() {
      if (this.model) this.model.position.y += 10;
    },
    moveDown() {
      if (this.model) this.model.position.y -= 10;
    },

    close() {
      if (this.model) {
        this.scene.remove(this.model);
        this.model = null;
        this.modelLoaded = false;
        this.draggableObjects = [];
      }
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
.modal-card-foot {
  justify-content: flex-end;
}

.stl-viewer-container {
  width: 100%;
  height: calc(100% - 77px);
}

.stl-viewer {
  width: 100%;
  height: 100%;
  /* border: 2px dashed #ccc; */
  position: relative;
  overflow: hidden;
}

.drop-hint {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: #666;
}

.drop-hint input {
  margin-top: 10px;
}

.controls {
  position: absolute;
  bottom: 10px;
  left: 10px;
  z-index: 100;
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}

.controls button {
  padding: 5px 10px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 3px;
  cursor: pointer;
}

.controls button:hover {
  background: #eee;
}
</style>
